import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { useGetStaticAssetUrl } from '@change-corgi/core/react/assets';
import { TrackingContext } from '@change-corgi/core/react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { PetitionEditPageCombinedState, PetitionEditPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { PetitionEditPageDataContextProvider } from './pageData';
import { PetitionSettingsPageDataContextProvider } from './pageSettingsData';
import { PetitionEditBannerStateProvider, PetitionEditPageStateContextProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<PetitionEditPageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: PetitionEditPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
	includeUndiscoverableTags?: boolean;
};

export const PetitionEditPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
	includeUndiscoverableTags = false,
}) => {
	const getStaticAssetUrl = useGetStaticAssetUrl();
	const pageContextState = usePageContext(slug, prefetchedData, includeUndiscoverableTags);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageSettingsData } = pageContextState;

	return (
		<PetitionEditPageDataContextProvider pageData={pageData}>
			<PetitionEditPageStateContextProvider
				petition={pageData.data.petition}
				defaultPersonPhotoUrl={getStaticAssetUrl('/profile-img/default-user-profile.svg')}
				defaultOrgPhotoUrl={getStaticAssetUrl('/profile-img/default-org-profile.svg')}
			>
				<PetitionEditBannerStateProvider>
					<PetitionSettingsPageDataContextProvider pageSettingsData={pageSettingsData}>
						<TrackingContext properties={{ petition_id: `petition_${pageData.data.petition.id}` }}>
							{children}
						</TrackingContext>
					</PetitionSettingsPageDataContextProvider>
				</PetitionEditBannerStateProvider>
			</PetitionEditPageStateContextProvider>
		</PetitionEditPageDataContextProvider>
	);
};
